import axios from 'axios';

const client = axios.create({ baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/ui/caf-inbox` });
client.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface CafMessageSummary {
  id: string; subject: string; sender: string; recipients: string[];
  sent_at: string | null; received_at: string | null; has_attachments: boolean;
  source: 'unknown' | 'purview' | 'graph'; folder: string | null; identity_conflict: boolean;
}
export interface CafAttachment { id: string; filename: string; content_type: string; size: number; sha256: string; }
export interface CafSource { source: 'unknown' | 'purview' | 'graph'; folder: string | null; source_id: string | null; location_subtype: string | null; }
export interface CafOriginal { source: CafSource['source']; message_id: string; format: 'msg' | 'eml'; }
export interface CafMessageDetail extends CafMessageSummary {
  body_text: string | null; body_html: string | null; internet_message_id: string | null;
  attachments: CafAttachment[]; sources: CafSource[];
  originals?: CafOriginal[];
}
export interface CafSearchParams { q?: string; source?: string; folder?: string; date_from?: string; date_to?: string; include_body?: boolean; page: number; limit: number; }
export interface CafSearchResponse { items: CafMessageSummary[]; total: number | null; has_more: boolean; page: number; limit: number; }
export interface CafFolder { source: string; folder: string | null; count: number; }
export interface CafImportResult { id: string; duplicate: boolean; identity_conflict: boolean; }

function errorCode(error: unknown): string {
  const code = (error as any)?.response?.data?.error;
  return typeof code === 'string' && code.length <= 64 ? code : 'request_failed';
}

async function request<T>(operation: () => any): Promise<T> {
  try { return (await operation()).data; } catch (error) { throw new Error(errorCode(error)); }
}

async function saveBlob(path: string, filename: string): Promise<void> {
  try {
    const response = await client.get<Blob>(path, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url; link.download = filename; link.rel = 'noopener';
    document.body.appendChild(link); link.click(); link.remove();
    URL.revokeObjectURL(url);
  } catch (error) { throw new Error(errorCode(error)); }
}

export const cafInboxApi = {
  searchMessages: async (params: CafSearchParams, signal?: AbortSignal): Promise<CafSearchResponse> => {
    const filters = { ...params };
    if (filters.date_from === '') delete filters.date_from;
    if (filters.date_to === '') delete filters.date_to;
    const started = Date.now();
    const timeout = filters.include_body === true ? 60000 : 15000;
    try {
      let response;
      try {
        // The project also loads legacy @types/axios; Axios 1.x supports signal.
        const config = { params: { ...filters, include_total: false }, signal, timeout };
        response = await client.get<CafSearchResponse>('/messages', config);
      } catch (error) {
        const legacy = (error as any)?.response?.status === 400 && errorCode(error) === 'invalid_request';
        if (!legacy || signal?.aborted) throw error;
        // Explicit scope must never be dropped or silently changed on an older
        // server. Only legacy callers omitting include_body may use this retry.
        if (filters.include_body !== undefined) throw new Error('search_options_rejected');
        const remaining = timeout - (Date.now() - started);
        if (remaining <= 0) throw new Error('search_timeout');
        const config = { params: filters, signal, timeout: remaining };
        response = await client.get<CafSearchResponse>('/messages', config);
      }
      const result = response.data;
      return { ...result, has_more: typeof result.has_more === 'boolean' ? result.has_more : typeof result.total === 'number' && result.page * result.limit < result.total };
    } catch (error) {
      if (signal?.aborted || (error as any)?.code === 'ERR_CANCELED') throw new Error('search_cancelled');
      if (['ECONNABORTED', 'ETIMEDOUT'].includes((error as any)?.code) || (error as Error)?.message === 'search_timeout') throw new Error('search_timeout');
      if ((error as Error)?.message === 'search_options_rejected') throw new Error('search_options_rejected');
      throw new Error(errorCode(error));
    }
  },
  getFolders: () => request<{ items: CafFolder[]; graph_enabled?: boolean }>(() => client.get('/folders')),
  getMessage: (id: string) => request<CafMessageDetail>(() => client.get(`/messages/${encodeURIComponent(id)}`)),
  downloadOriginal: (id: string, filename: string) => saveBlob(`/messages/${encodeURIComponent(id)}/original`, filename),
  downloadAttachment: (id: string, attachmentId: string, filename: string) => saveBlob(`/messages/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`, filename),
  importMessage: (file: File, provenance?: Record<string, unknown>) => {
    const data = new FormData(); data.append('file', file);
    if (provenance) data.append('provenance', JSON.stringify(provenance));
    return request<CafImportResult>(() => client.post('/imports', data));
  },
};
